import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { layoutControls, moveLayoutItem, resolvedFilterGroups } from './filterLayout';
import type { FilterGroupLayout, LensDocument } from './types';
import type { BoardEditor } from './useBoardEdit';

type Dragged = { kind: 'group' | 'control'; id: string };
const DRAG_TYPE = 'application/lens-filter-layout';
const compactInput = { '& .MuiInputBase-root': { fontSize: 13 }, '& .MuiInputBase-input': { py: 0.85 } };

export default function FilterLayoutEditor({
  document,
  editor,
  onClose,
}: {
  document: LensDocument;
  editor: BoardEditor;
  onClose: () => void;
}) {
  const [initial] = useState(() => resolvedFilterGroups(document));
  const [groups, setGroups] = useState(initial);
  const [selected, setSelected] = useState(initial[0]?.id ?? 'ungrouped');
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [dragged, setDragged] = useState<Dragged>();
  const controls = layoutControls(document);
  const byId = new Map(controls.map((control) => [control.id, control]));
  const group = groups.find((entry) => entry.id === selected) ?? groups[0];
  const groupIndex = groups.findIndex((entry) => entry.id === group.id);
  const matched = group.controls.filter((id) => byId.get(id)?.label.toLowerCase().includes(query.trim().toLowerCase()));
  const labels = groups.map((entry) => entry.label.trim().toLowerCase());
  const invalid = labels.some((label, index) => !label || labels.indexOf(label) !== index);
  const changed =
    JSON.stringify(groups.map((entry) => ({ ...entry, label: entry.label.trim() }))) !==
    JSON.stringify(document.display.filterGroups);
  const patchGroup = (id: string, patch: Partial<FilterGroupLayout>) =>
    setGroups((current) => current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  const moveControl = (id: string, target: string, before?: string) =>
    setGroups((current) =>
      current.map((entry) => {
        const kept = entry.controls.filter((ref) => ref !== id);
        if (entry.id !== target) return { ...entry, controls: kept };
        const at = before ? kept.indexOf(before) : -1;
        kept.splice(at < 0 ? kept.length : at, 0, id);
        return { ...entry, controls: kept };
      }),
    );
  const reorderGroup = (id: string, to: number) =>
    setGroups((current) =>
      moveLayoutItem(
        current,
        current.findIndex((entry) => entry.id === id),
        to,
      ),
    );
  const startDrag = (event: React.DragEvent, item: Dragged) => {
    event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
    setDragged(item);
  };
  const dropOnGroup = (event: React.DragEvent, target: string) => {
    event.preventDefault();
    if (!dragged) return;
    if (dragged.kind === 'control') moveControl(dragged.id, target);
    else
      reorderGroup(
        dragged.id,
        groups.findIndex((entry) => entry.id === target),
      );
    setDragged(undefined);
  };
  const addGroup = () => {
    let label = 'New group';
    for (let suffix = 2; labels.includes(label.toLowerCase()); suffix++) label = `New group ${suffix}`;
    const id = `group-${crypto.randomUUID()}`;
    setGroups((current) => [
      ...current.filter((entry) => entry.id !== 'ungrouped'),
      { id, label, controls: [], collapsed: false },
      ...current.filter((entry) => entry.id === 'ungrouped'),
    ]);
    setSelected(id);
    setQuery('');
  };
  const removeGroup = () => {
    setGroups((current) =>
      current
        .filter((entry) => entry.id !== group.id)
        .map((entry) =>
          entry.id === 'ungrouped' ? { ...entry, controls: [...entry.controls, ...group.controls] } : entry,
        ),
    );
    setSelected('ungrouped');
    setQuery('');
  };
  const save = () => {
    setSaving(true);
    setFailed(false);
    editor.apply(
      {
        op: 'set',
        id: 'lens',
        patch: { display: { filterGroups: groups.map((entry) => ({ ...entry, label: entry.label.trim() })) } },
      },
      (applied) => {
        setSaving(false);
        if (applied) onClose();
        else setFailed(true);
      },
    );
  };

  return (
    <Dialog aria-labelledby="filter-layout-title" fullWidth maxWidth="md" onClose={saving ? undefined : onClose} open>
      <DialogTitle id="filter-layout-title" sx={{ pb: 1.5, pr: 6 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 20, fontWeight: 600 }}>
          Organise filters
        </Typography>
        <Typography component="span" color="text.secondary" sx={{ display: 'block', fontSize: 13, mt: 0.5 }}>
          {document.title} · Layout for this Lens
        </Typography>
        <IconButton
          aria-label="Close filter layout"
          disabled={saving}
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            height: { xs: '65vh', sm: 540 },
            minHeight: 0,
            opacity: saving ? 0.6 : 1,
            pointerEvents: saving ? 'none' : undefined,
          }}
        >
          <Stack
            sx={{
              width: { sm: 245 },
              flexShrink: 0,
              bgcolor: 'background.default',
              borderRight: { sm: 1 },
              borderColor: { sm: 'divider' },
              minHeight: 0,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>GROUPS</Typography>
              <Button
                onClick={addGroup}
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                sx={{ fontSize: 12 }}
              >
                Add group
              </Button>
            </Stack>
            <List aria-label="Filter groups" dense sx={{ p: 1, pt: 0, overflow: 'auto', flex: 1 }}>
              {groups.map((entry) => (
                <ListItemButton
                  aria-label={`Edit ${entry.label} group`}
                  draggable
                  key={entry.id}
                  onClick={() => {
                    setSelected(entry.id);
                    setQuery('');
                  }}
                  onDragEnd={() => setDragged(undefined)}
                  onDragOver={(event) => event.preventDefault()}
                  onDragStart={(event) => startDrag(event, { kind: 'group', id: entry.id })}
                  onDrop={(event) => dropOnGroup(event, entry.id)}
                  selected={selected === entry.id}
                  sx={{
                    borderRadius: 1,
                    py: 1.15,
                    px: 1,
                    gap: 0.75,
                    '&.Mui-selected': { bgcolor: 'action.selected', color: 'primary.main' },
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 17, opacity: 0.5, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: 13,
                      flex: 1,
                      overflowWrap: 'anywhere',
                      fontWeight: selected === entry.id ? 600 : 400,
                    }}
                  >
                    {entry.label || 'Unnamed group'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    {entry.controls.length}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
            <Typography color="text.secondary" sx={{ fontSize: 11.5, px: 2, py: 1.5 }}>
              Drag to reorder, or use the arrow buttons.
            </Typography>
          </Stack>
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Stack spacing={1} sx={{ p: 2, pb: 1.25, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TextField
                  aria-label="Group name"
                  error={
                    !group.label.trim() ||
                    labels.filter((label) => label === group.label.trim().toLowerCase()).length > 1
                  }
                  fullWidth
                  inputProps={{ 'aria-label': 'Group name', maxLength: 60 }}
                  onChange={(event) => patchGroup(group.id, { label: event.target.value })}
                  size="small"
                  sx={compactInput}
                  value={group.label}
                  disabled={group.id === 'ungrouped'}
                />
                <Tooltip title="Move group up">
                  <span>
                    <IconButton
                      aria-label={`Move ${group.label} group up`}
                      disabled={groupIndex === 0}
                      onClick={() => reorderGroup(group.id, groupIndex - 1)}
                      size="small"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move group down">
                  <span>
                    <IconButton
                      aria-label={`Move ${group.label} group down`}
                      disabled={groupIndex === groups.length - 1}
                      onClick={() => reorderGroup(group.id, groupIndex + 1)}
                      size="small"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove group; its filters move to Ungrouped">
                  <span>
                    <IconButton
                      aria-label={`Remove ${group.label} group`}
                      disabled={group.id === 'ungrouped'}
                      onClick={removeGroup}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!group.collapsed}
                      onChange={(event) => patchGroup(group.id, { collapsed: !event.target.checked })}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontSize: 12 }}>Start expanded</Typography>}
                  sx={{ m: 0, ml: -0.75 }}
                />
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  {group.controls.length} filters and inputs
                </Typography>
              </Stack>
              <TextField
                fullWidth
                inputProps={{ 'aria-label': 'Find a filter to organise' }}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a filter in this group"
                size="small"
                sx={compactInput}
                value={query}
              />
            </Stack>
            <Box
              onDragOver={(event) => {
                if (dragged?.kind === 'control') event.preventDefault();
              }}
              onDrop={(event) => dropOnGroup(event, group.id)}
              sx={{ overflow: 'auto', flex: 1, px: 2, py: 0.5 }}
            >
              {matched.map((id) => {
                const control = byId.get(id)!;
                const index = group.controls.indexOf(id);
                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    draggable
                    key={id}
                    onDragEnd={() => setDragged(undefined)}
                    onDragStart={(event) => startDrag(event, { kind: 'control', id })}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (dragged?.kind === 'control' && dragged.id !== id) moveControl(dragged.id, group.id, id);
                      setDragged(undefined);
                    }}
                    spacing={0.75}
                    sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <DragIndicatorIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, overflowWrap: 'anywhere' }}>{control.label}</Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 10.5 }}>
                        {control.kind}
                      </Typography>
                    </Stack>
                    <TextField
                      inputProps={{ 'aria-label': `Group for ${control.label}` }}
                      onChange={(event) => moveControl(id, event.target.value)}
                      select
                      size="small"
                      sx={{ ...compactInput, width: 145, flexShrink: 0 }}
                      value={group.id}
                    >
                      {groups.map((entry) => (
                        <MenuItem key={entry.id} value={entry.id} sx={{ fontSize: 12 }}>
                          {entry.label || 'Unnamed group'}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Stack direction="row" spacing={0}>
                      <IconButton
                        aria-label={`Move ${control.label} up`}
                        disabled={Boolean(query.trim()) || index === 0}
                        onClick={() =>
                          patchGroup(group.id, { controls: moveLayoutItem(group.controls, index, index - 1) })
                        }
                        size="small"
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                      <IconButton
                        aria-label={`Move ${control.label} down`}
                        disabled={Boolean(query.trim()) || index === group.controls.length - 1}
                        onClick={() =>
                          patchGroup(group.id, { controls: moveLayoutItem(group.controls, index, index + 1) })
                        }
                        size="small"
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                );
              })}
              {!matched.length ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ py: 6, px: 2, textAlign: 'center' }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {query ? 'No matching filters' : 'This group is ready for filters'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    {query
                      ? 'Try another name or choose a different group.'
                      : 'Choose another group, then use a filter’s group menu to move it here. You can also drag a filter onto this group.'}
                  </Typography>
                </Stack>
              ) : null}
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      {invalid || failed ? (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
          {invalid
            ? 'Give every group a unique, non-empty name.'
            : 'The layout could not be saved. Your edits are still here; try again.'}
        </Alert>
      ) : null}
      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Chip label="This Lens only" size="small" variant="outlined" sx={{ fontSize: 11.5 }} />
        <Stack direction="row" spacing={1}>
          <Button disabled={saving} onClick={onClose} variant="text">
            Cancel
          </Button>
          <Button disabled={saving || editor.pending || invalid || !changed} onClick={save} variant="contained">
            {saving ? 'Saving…' : 'Save layout'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
